import classNames from 'classnames';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { Container } from 'react-bootstrap';

import { Loader } from 'src/components/common/loader/loader';
import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import { BirthtimeStep } from 'src/components/main/quiz/birthtime-step/birthtime-step';
import { DiscoverSliderStep } from 'src/components/main/quiz/dicover-slider-step/discover-slider-step';
import HometownStep from 'src/components/main/quiz/hometown-step/hometown-step';
import { IntroStep } from 'src/components/main/quiz/intro-step/intro-step';
import NameStep from 'src/components/main/quiz/name-step/name-step';
import { OnboardingHeader } from 'src/components/main/quiz/onboarding-header';
import SignHelped from 'src/components/main/quiz/sign-helped-step/sign-helped';
import { TakeCommand } from 'src/components/main/quiz/take-command';
import { WeGotYouStep } from 'src/components/main/quiz/we-got-you-step';
import { WelcomeScreenABTest } from 'src/components/main/quiz/welcome-screen-ab-test';
import { WelcomeScreenFourVariantsABTest } from 'src/components/main/quiz/welcome-screen-four-variants-ab-test';
import { WelcomeScreenThreeVariantsABTest } from 'src/components/main/quiz/welcome-screen-three-variants-ab-test';
import { WelcomeScreen } from 'src/components/main/quiz/welcome-screen/welcome-screen';
import {
  FIRST_SCREEN_AB_TEST_NAME,
  FIRST_SCREEN_FOUR_VARIANTS_AB_TEST_NAME,
  FIRST_SCREEN_THREE_VARIANTS_AB_TEST_NAME,
  FirstScreenAbTestGroupName,
  FirstScreenFourVariantsAbTestGroupName,
  FirstScreenThreeVariantsAbTestGroupName,
  RESULT_SCREENS_AB_TEST_NAME,
  ResultScreensAbTestGroupName,
} from 'src/constants/analytics';
import { routes } from 'src/constants/navigation';
import {
  QuizSection,
  QuizStep,
  QuizStepType,
  Step,
  isStepProgressShown,
} from 'src/constants/quiz';
import {
  QUIZ_STEPS_INTRODUCTION,
  QUIZ_STEPS_MOOD,
  QUIZ_STEPS_PERSONALIZATION,
  QUIZ_STEPS_PURPOSE,
  QUIZ_STEPS_RELATIONSHIP,
  STEPS,
} from 'src/constants/quiz-steps';
import { getIpInfo } from 'src/utils/get-ip-info';
import { useAbTestCaseName } from 'src/utils/hooks/use-ab-test-case-name';

import { useAnalytics } from '@features/Analytics';

import PageLoading from '../../components/common/page-loading/page-loading';
import Title from '../../components/common/title/title';
import DateOfBirthStep from '../../components/main/quiz/date-of-birth-step/date-of-birth-step';
import EnterEmailStep from '../../components/main/quiz/enter-email-step/enter-email-step';
import FinalSummaryStep from '../../components/main/quiz/final-summary-step/final-summary-step';
import StepHeader from '../../components/main/quiz/step-header/step-header';
import nonMemberPage from '../../components/pages/non-member-page/non-member-page';
import styles from './index.module.scss';

const SingleOptionSelectStep = dynamic(
  () =>
    import(
      '../../components/main/quiz/single-option-select-step/single-option-select-step'
    ),
  {
    ssr: false,
    loading: () => <Loader />,
  },
);

// const MultipleOptionSelectStep = dynamic(
//   () =>
//     import(
//       '../../components/main/quiz/multiple-option-select-step/multiple-option-select-step'
//     ),
//   {
//     ssr: false,
//     loading: () => <Loader />,
//   },
// );

const TextCardWithBackgroundStep = dynamic(
  () =>
    import(
      '../../components/main/quiz/text-card-with-background-step/text-card-with-background-step'
    ),
  {
    ssr: false,
    loading: () => <Loader />,
  },
);

const BestMatchesStep = dynamic(
  () =>
    import('../../components/main/quiz/best-matches-step/best-matches-step'),
  {
    ssr: false,
    loading: () => <Loader />,
  },
);

const PartnerCompatibilityStep = dynamic(
  () =>
    import(
      '../../components/main/quiz/partner-compatibility-step/partner-compatibility-step'
    ),
  {
    ssr: false,
    loading: () => <Loader />,
  },
);

const AnimatedScreensStep = dynamic(
  () =>
    import(
      '../../components/main/quiz/animated-screens-step/animated-screens-step'
    ),
  {
    ssr: false,
    loading: () => <Loader />,
  },
);

const PersonalPlanHighlights = dynamic(
  () =>
    import(
      '../../components/main/quiz/personal-plan-highlights/personal-plan-highlights'
    ),
  {
    ssr: false,
    loading: () => <Loader />,
  },
);

const getStepsWithShownProgressCount = (steps: Step[]) =>
  steps.reduce((count, step) => count + +isStepProgressShown(step), 0);

const Quiz = observer(() => {
  const {
    quizStore,
    authStore: { access_code, fetchUser, auth_token, variant },
  } = useStores();

  const { trackPinterestEvent, trackGoogleEvent } = useAnalytics();

  const showNewResultsScreens =
    useAbTestCaseName(RESULT_SCREENS_AB_TEST_NAME) ===
    ResultScreensAbTestGroupName.NEBULA_LIKE;

  const showNewFirstScreen =
    useAbTestCaseName(FIRST_SCREEN_AB_TEST_NAME) ===
    FirstScreenAbTestGroupName.NEW;

  const showNewFirstScreenFourVariants =
    useAbTestCaseName(FIRST_SCREEN_FOUR_VARIANTS_AB_TEST_NAME) ===
    FirstScreenFourVariantsAbTestGroupName.NEW;

  const showNewFirstScreenThreeVariants =
    useAbTestCaseName(FIRST_SCREEN_THREE_VARIANTS_AB_TEST_NAME) ===
    FirstScreenThreeVariantsAbTestGroupName.NEW;

  const steps = useMemo(() => {
    let QUIZ_STEPS = [];

    QUIZ_STEPS.push(
      ...QUIZ_STEPS_INTRODUCTION.map((step) => STEPS[step]).filter((step) => {
        switch (step.step) {
          case QuizStep.WELCOME: {
            return (
              !showNewFirstScreen &&
              !showNewFirstScreenFourVariants &&
              !showNewFirstScreenThreeVariants
            );
          }
          case QuizStep.WELCOME_AB_TEST: {
            return showNewFirstScreen;
          }
          case QuizStep.WELCOME_FOUR_VARIANTS_AB_TEST: {
            return showNewFirstScreenFourVariants;
          }
          case QuizStep.WELCOME_THREE_VARIANTS_AB_TEST: {
            return showNewFirstScreenThreeVariants;
          }
          default: {
            return true;
          }
        }
      }),
      ...QUIZ_STEPS_PURPOSE.map((step) => STEPS[step]).filter((step) => {
        switch (step.step) {
          default: {
            return true;
          }
        }
      }),
      ...QUIZ_STEPS_MOOD.map((step) => STEPS[step]).filter((step) => {
        switch (step.step) {
          default: {
            return true;
          }
        }
      }),
      ...QUIZ_STEPS_PERSONALIZATION.map((step) => STEPS[step]).filter(
        (step) => {
          switch (step.step) {
            case QuizStep.NO_PROBLEM: {
              return (
                quizStore.birthtimeKnowledge === 1 ||
                quizStore.birthtimeKnowledge === undefined
              );
            }
            case QuizStep.BIRTH_TIME: {
              return quizStore.birthtimeKnowledge === 0;
            }
            case QuizStep.GREETINGS: {
              return quizStore.name !== '';
            }
            default: {
              return true;
            }
          }
        },
      ),
      ...QUIZ_STEPS_RELATIONSHIP.map((step) => STEPS[step]).filter((step) => {
        switch (step.step) {
          case QuizStep.PARTNER_ASTROLOGY_SIGN:
          case QuizStep.PARTNER_BIRTH_TIME_KNOWLEDGE:
          case QuizStep.DISCUSSING_PROBLEMS:
          case QuizStep.SEX_RELATIONSHIP:
          case QuizStep.AFFECT_RELATIONSHIP: {
            return quizStore.relationship === 1;
          }
          case QuizStep.PARTNER_COMPATIBILITY: {
            return (
              quizStore.partnerAstrologySign !== undefined &&
              quizStore.relationship === 1 &&
              !showNewResultsScreens
            );
          }
          case QuizStep.SEX:
          case QuizStep.COMFORTABLE:
          case QuizStep.GOOD_ENOUGH:
          case QuizStep.RELATIONSHIP_GOAL:
          case QuizStep.LONG_TERM: {
            return quizStore.relationship !== 1 && quizStore.relationship !== 4;
          }
          case QuizStep.BEST_MATCHES: {
            return quizStore.relationship !== 1 && !showNewResultsScreens;
          }
          case QuizStep.PARTNER_BIRTH_TIME:
          case QuizStep.PARTNER_HOMETOWN: {
            return (
              quizStore.partnerBirthtimeKnowledge === 0 &&
              quizStore.relationship === 1
            );
          }
          case QuizStep.PARTNER_NO_PROBLEM: {
            return (
              quizStore.partnerBirthtimeKnowledge === 1 &&
              quizStore.relationship === 1
            );
          }
          case QuizStep.ANIMATION_SCREENS:
          case QuizStep.PERSONAL_PLAN_HIGHLIGHTS: {
            return showNewResultsScreens;
          }
          default: {
            return true;
          }
        }
      }),
    );
    return QUIZ_STEPS;
  }, [
    quizStore.birthtimeKnowledge,
    quizStore.name,
    quizStore.partnerAstrologySign,
    quizStore.partnerBirthtimeKnowledge,
    quizStore.relationship,
    showNewFirstScreen,
    showNewFirstScreenFourVariants,
    showNewFirstScreenThreeVariants,
    showNewResultsScreens,
  ]);

  const router = useRouter();
  const { pathname, query, isReady } = router;

  const stepIndexParameterValue = query['step'];
  const [stepIndex, isStepIndexValid] = useMemo(
    () => {
      if (!isReady || !steps) {
        return [0, false];
      }

      const isStepCompleted = (stepIndex: number): boolean => {
        let isCompleted: boolean;
        const step = steps[stepIndex];
        switch (step.step) {
          case QuizStep.WELCOME_AB_TEST: {
            isCompleted = quizStore.welcomeStepCompleted;
            break;
          }
          case QuizStep.WELCOME_FOUR_VARIANTS_AB_TEST: {
            isCompleted = quizStore.welcomeStepCompleted;
            break;
          }
          case QuizStep.WELCOME_THREE_VARIANTS_AB_TEST: {
            isCompleted = quizStore.welcomeStepCompleted;
            break;
          }
          case QuizStep.FACING_REPEATING: {
            isCompleted = quizStore.facingRepeatingStepCompleted;
            break;
          }
          case QuizStep.ASPECT: {
            isCompleted = quizStore.aspectStepCompleted;
            break;
          }
          case QuizStep.DESIRED_RELATIONSHIP: {
            isCompleted = quizStore.desiredRelationshipStepCompleted;
            break;
          }
          case QuizStep.PREVIOUS_RELATIONSHIP: {
            isCompleted = quizStore.previousRelationshipStepCompleted;
            break;
          }
          case QuizStep.TOXIC_RELATIONSHIP: {
            isCompleted = quizStore.toxicRelationshipStepCompleted;
            break;
          }
          case QuizStep.IN_LOVE: {
            isCompleted = quizStore.inLoveStepCompleted;
            break;
          }
          case QuizStep.ROMANTIC_FEELINGS: {
            isCompleted = quizStore.romanticFeelingsStepCompleted;
            break;
          }
          case QuizStep.KARMIC_LESSONS: {
            isCompleted = quizStore.karmicLessonsStepCompleted;
            break;
          }
          case QuizStep.PURPOSE: {
            isCompleted = quizStore.purposeStepCompleted;
            break;
          }
          case QuizStep.JOB_LOVING: {
            isCompleted = quizStore.jobLovingStepCompleted;
            break;
          }
          case QuizStep.FINANCIAL_GOAL: {
            isCompleted = quizStore.financialGoalStepCompleted;
            break;
          }
          case QuizStep.LIFE_ASPECTS: {
            isCompleted = quizStore.lifeAspectsStepCompleted;
            break;
          }
          case QuizStep.WORK_OUT: {
            isCompleted = quizStore.workOutStepCompleted;
            break;
          }
          case QuizStep.SPIRITUAL_GOAL: {
            isCompleted = quizStore.spiritualGoalStepCompleted;
            break;
          }
          case QuizStep.LIFE: {
            isCompleted = quizStore.lifeStepCompleted;
            break;
          }
          case QuizStep.MOOD: {
            isCompleted = quizStore.moodStepCompleted;
            break;
          }
          case QuizStep.CRITICS_SENSITIVE: {
            isCompleted = quizStore.criticsSensitiveStepCompleted;
            break;
          }
          case QuizStep.HOT_TEMPRED: {
            isCompleted = quizStore.hotTempredStepCompleted;
            break;
          }
          case QuizStep.MOOD_SWINGS: {
            isCompleted = quizStore.moodSwingsStepCompleted;
            break;
          }
          case QuizStep.EMOTIONS: {
            isCompleted = quizStore.emotionsStepCompleted;
            break;
          }
          case QuizStep.RELATIONSHIP: {
            isCompleted = quizStore.relationshipStepCompleted;
            break;
          }
          case QuizStep.GENDER: {
            isCompleted = quizStore.genderStepCompleted;
            break;
          }
          case QuizStep.NAME: {
            isCompleted = quizStore.nameStepCompleted;
            break;
          }
          case QuizStep.DATE_OF_BIRTH: {
            isCompleted = quizStore.dateOfBirthStepCompleted;
            break;
          }
          case QuizStep.HOMETOWN: {
            isCompleted = quizStore.hometownStepCompleted;
            break;
          }
          case QuizStep.BIRTH_TIME_KNOWLEDGE: {
            isCompleted = quizStore.birthtimeKnowledgeStepCompleted;
            break;
          }
          case QuizStep.BIRTH_TIME: {
            isCompleted = quizStore.birthtimeStepCompleted;
            break;
          }
          case QuizStep.SEX: {
            isCompleted = quizStore.sexStepCompleted;
            break;
          }
          case QuizStep.COMFORTABLE: {
            isCompleted = quizStore.comfortableStepCompleted;
            break;
          }
          case QuizStep.GOOD_ENOUGH: {
            isCompleted = quizStore.goodEnoughStepCompleted;
            break;
          }
          case QuizStep.RELATIONSHIP_GOAL: {
            isCompleted = quizStore.relationshipGoalStepCompleted;
            break;
          }
          case QuizStep.LONG_TERM: {
            isCompleted = quizStore.longTermStepCompleted;
            break;
          }
          case QuizStep.PARTNER_BIRTH_TIME_KNOWLEDGE: {
            isCompleted = quizStore.partnerBirthtimeKnowledgeStepCompleted;
            break;
          }
          case QuizStep.PARTNER_BIRTH_TIME: {
            isCompleted = quizStore.partnerBirthtimeStepCompleted;
            break;
          }
          case QuizStep.PARTNER_HOMETOWN: {
            isCompleted = quizStore.partnerHometownStepCompleted;
            break;
          }
          case QuizStep.DISCUSSING_PROBLEMS: {
            isCompleted = quizStore.discussingProblemsStepCompleted;
            break;
          }
          case QuizStep.AFFECT_RELATIONSHIP: {
            isCompleted = quizStore.affectRelationshipStepCompleted;
            break;
          }
          case QuizStep.SEX_RELATIONSHIP: {
            isCompleted = quizStore.sexRelationshipStepCompleted;
            break;
          }
          case QuizStep.ENTER_EMAIL: {
            isCompleted = access_code !== '';
            break;
          }
          default: {
            isCompleted = true;
            break;
          }
        }
        return (
          isCompleted && (stepIndex === 0 || isStepCompleted(stepIndex - 1))
        );
      };
      let stepIndex =
        stepIndexParameterValue === 'last'
          ? steps.length - 1
          : +(stepIndexParameterValue ?? Number.NaN) - 1;
      stepIndex =
        !Number.isInteger(stepIndex) || stepIndex < 0
          ? 0
          : Math.min(stepIndex, steps.length - 1);

      // Checking whether the user completed all the previous steps
      // to not allow them to skip the quiz by modifying the URL
      while (stepIndex > 0 && !isStepCompleted(stepIndex - 1)) {
        stepIndex--;
      }
      if (
        stepIndexParameterValue != null &&
        +stepIndexParameterValue - 1 !== stepIndex
      ) {
        // noinspection JSIgnoredPromiseFromCall
        router.replace(
          {
            pathname,
            query: stepIndex > 0 ? { step: stepIndex + 1 } : undefined,
          },
          undefined,
          { shallow: true },
        );
        return [stepIndex, false];
      }
      return [stepIndex, true];
    },
    // https://github.com/vercel/next.js/issues/18127
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps, quizStore, pathname, stepIndexParameterValue],
  );
  const step = steps?.[stepIndex];

  const setStepIndex = useCallback(
    (stepIndex: number, replaceRoute = false) => {
      // noinspection JSIgnoredPromiseFromCall
      router[replaceRoute ? 'replace' : 'push']({
        pathname,
        query: stepIndex > 0 ? { step: stepIndex + 1 } : undefined,
      });
    },
    [pathname, router],
  );

  const goToPreviousStep = (replaceRoute?: boolean) => {
    setStepIndex(stepIndex - 1, replaceRoute);
  };
  const goToNextStep = useCallback(
    (replaceRoute?: boolean) => {
      const goToNextPage = () => {
        // noinspection JSIgnoredPromiseFromCall
        router.push(routes.newPlanPaywall);
      };
      if (stepIndex < steps!.length - 1) {
        setStepIndex(stepIndex + 1, replaceRoute);
      } else {
        fetchUser({ token: auth_token }).then((r) => r);
        goToNextPage();
      }
    },
    [stepIndex, steps, router, setStepIndex, fetchUser, auth_token],
  );

  const canSkipQuiz = process.env.NEXT_PUBLIC_ENV === 'development';
  const handleQuizSkipClick = () => {
    quizStore.fillInAllSteps();
    setStepIndex(steps.indexOf(STEPS[QuizStep.ENTER_EMAIL]));
  };

  useEffect(() => {
    (async () => {
      if (!quizStore.country) {
        const { country, countryCode } = await getIpInfo();
        if (country) {
          quizStore.setCountry(country);
          quizStore.setCountryCode(countryCode);
        } else {
          quizStore.setCountry('US');
          quizStore.setCountryCode('US');
        }
      }
    })();
  }, [quizStore]);

  const currentFirstScreen = showNewFirstScreen
    ? QuizStep.WELCOME_AB_TEST
    : showNewFirstScreenFourVariants
      ? QuizStep.WELCOME_FOUR_VARIANTS_AB_TEST
      : showNewFirstScreenThreeVariants
        ? QuizStep.WELCOME_THREE_VARIANTS_AB_TEST
        : QuizStep.WELCOME;

  useEffect(() => {
    if (
      isReady &&
      step.step === currentFirstScreen &&
      query['reset'] === undefined
    ) {
      trackGoogleEvent({
        eventName: 'test_variant',
        options: { variant: variant ?? 'default' },
      });
      trackPinterestEvent({ eventName: 'PageVisit' });
    }
  }, [
    currentFirstScreen,
    isReady,
    query,
    step.step,
    trackGoogleEvent,
    trackPinterestEvent,
    variant,
  ]);

  useEffect(() => {
    if (step && isStepIndexValid) {
      switch (step.step) {
        case QuizStep.WELCOME: {
          trackGoogleEvent({ eventName: 'onboarding_start' });
          trackGoogleEvent({
            eventName: 'onboarding_ask_relationship_opened',
          });
          break;
        }
        case QuizStep.WELCOME_AB_TEST: {
          trackGoogleEvent({ eventName: 'onboarding_start' });
          trackGoogleEvent({
            eventName: 'onboarding_remove_loneliness_opened',
          });
          break;
        }
        case QuizStep.WELCOME_FOUR_VARIANTS_AB_TEST: {
          trackGoogleEvent({ eventName: 'onboarding_start' });
          trackGoogleEvent({
            eventName: 'onboarding_find_perfect_match_opened',
          });
          break;
        }
        case QuizStep.WELCOME_THREE_VARIANTS_AB_TEST: {
          trackGoogleEvent({ eventName: 'onboarding_start' });
          trackGoogleEvent({
            eventName: 'onboarding_eclipses_work_u_opened',
          });
          break;
        }
        case QuizStep.INTRO: {
          trackGoogleEvent({
            eventName: 'onboarding_improve_profile_fit_opened',
          });
          break;
        }
        case QuizStep.FACING_REPEATING: {
          trackGoogleEvent({
            eventName: 'onboarding_repeating_pattern_opened',
          });
          break;
        }
        case QuizStep.ASPECT: {
          trackGoogleEvent({
            eventName: 'onboarding_aspect_relationship_opened',
          });
          break;
        }
        case QuizStep.DESIRED_RELATIONSHIP: {
          trackGoogleEvent({
            eventName: 'onboarding_desired_relationship_type_opened',
          });
          break;
        }
        case QuizStep.PREVIOUS_RELATIONSHIP: {
          trackGoogleEvent({
            eventName: 'onboarding_describes_prev_relationship_opened',
          });
          break;
        }
        case QuizStep.TOXIC_RELATIONSHIP: {
          trackGoogleEvent({
            eventName: 'onboarding_toxic_relationship_aspect_opened',
          });
          break;
        }
        case QuizStep.IN_LOVE: {
          trackGoogleEvent({
            eventName: 'onboarding_love_now_opened',
          });
          break;
        }
        case QuizStep.ROMANTIC_FEELINGS: {
          trackGoogleEvent({
            eventName: 'onboarding_navigate_romantic_opened',
          });
          break;
        }
        case QuizStep.KARMIC_LESSONS: {
          trackGoogleEvent({
            eventName: 'onboarding_know_karmic_opened',
          });
          break;
        }
        case QuizStep.DISCOVER_SLIDER: {
          trackGoogleEvent({
            eventName: 'onboarding_decode_moons_influence_opened',
          });
          break;
        }
        case QuizStep.PURPOSE: {
          trackGoogleEvent({
            eventName: 'onboarding_your_life_purpose_opened',
          });
          break;
        }
        case QuizStep.JOB_LOVING: {
          trackGoogleEvent({
            eventName: 'onboarding_you_happy_your_job_opened',
          });
          break;
        }
        case QuizStep.FINANCIAL_GOAL: {
          trackGoogleEvent({
            eventName: 'onboarding_financial_goal_opened',
          });
          break;
        }
        case QuizStep.LIFE_ASPECTS: {
          trackGoogleEvent({
            eventName: 'onboarding_what_add_your_profile_opened',
          });
          break;
        }
        case QuizStep.WORK_OUT: {
          trackGoogleEvent({
            eventName: 'onboarding_what_issues_work_opened',
          });
          break;
        }
        case QuizStep.SPIRITUAL_GOAL: {
          trackGoogleEvent({
            eventName: 'onboarding_lifes_spiritual_goal_opened',
          });
          break;
        }
        case QuizStep.WE_GOT_YOU: {
          trackGoogleEvent({
            eventName: 'onboarding_we_got_you_opened',
          });
          break;
        }
        case QuizStep.LIFE: {
          trackGoogleEvent({ eventName: 'onboarding_life_now_opened' });
          break;
        }
        case QuizStep.MOOD: {
          trackGoogleEvent({
            eventName: 'onboarding_felling_today_opened',
          });
          break;
        }
        case QuizStep.CRITICS_SENSITIVE: {
          trackGoogleEvent({
            eventName: 'onboarding_sensitive_disapproval_opened',
          });
          break;
        }
        case QuizStep.HOT_TEMPRED: {
          trackGoogleEvent({
            eventName: 'onboarding_hot_tempered_person_opened',
          });
          break;
        }
        case QuizStep.MOOD_SWINGS: {
          trackGoogleEvent({
            eventName: 'onboarding_experience_mood_opened',
          });
          break;
        }
        case QuizStep.TAKE_COMMAND: {
          trackGoogleEvent({
            eventName: 'onboarding_command_your_life_opened',
          });
          break;
        }
        case QuizStep.EMOTIONS: {
          trackGoogleEvent({ eventName: 'onboarding_emotions_opened' });
          break;
        }
        case QuizStep.RELATIONSHIP: {
          trackGoogleEvent({ eventName: 'onboarding_relationship_opened' });
          break;
        }
        case QuizStep.PARTNER_ASTROLOGY_SIGN: {
          trackGoogleEvent({ eventName: 'onboarding_partner_sign_opened' });
          break;
        }
        case QuizStep.PARTNER_COMPATIBILITY: {
          trackGoogleEvent({
            eventName: 'onboarding_compatibility_opened',
          });
          break;
        }
        case QuizStep.BEST_MATCHES: {
          trackGoogleEvent({ eventName: 'onboarding_best_matches_opened' });
          break;
        }
        case QuizStep.GENDER: {
          trackGoogleEvent({ eventName: 'onboarding_gender_opened' });
          break;
        }
        case QuizStep.NAME: {
          trackGoogleEvent({ eventName: 'onboarding_name_opened' });
          break;
        }
        case QuizStep.GREETINGS: {
          trackGoogleEvent({ eventName: 'onboarding_welcome_opened' });
          break;
        }
        case QuizStep.DATE_OF_BIRTH: {
          trackGoogleEvent({
            eventName: 'onboarding_date_of_birth_opened',
          });
          break;
        }
        case QuizStep.BIRTH_TIME_KNOWLEDGE: {
          trackGoogleEvent({
            eventName: 'onboarding_birthtime_knowledge_opened',
          });
          break;
        }
        case QuizStep.BIRTH_TIME: {
          trackGoogleEvent({
            eventName: 'onboarding_birthtime_opened',
          });
          break;
        }
        case QuizStep.SIGN_HELPED: {
          trackGoogleEvent({
            eventName: 'onboarding_sign_helped_opened',
          });
          break;
        }
        case QuizStep.NO_PROBLEM: {
          trackGoogleEvent({
            eventName: 'onboarding_no_problem_opened',
          });
          break;
        }
        case QuizStep.SEX: {
          trackGoogleEvent({ eventName: 'onboarding_sex_priority_opened' });
          break;
        }
        case QuizStep.COMFORTABLE: {
          trackGoogleEvent({
            eventName: 'onboarding_comfortable_unfamiliar_people_opened',
          });
          break;
        }
        case QuizStep.GOOD_ENOUGH: {
          trackGoogleEvent({ eventName: 'onboarding_good_enough_opened' });
          break;
        }
        case QuizStep.RELATIONSHIP_GOAL: {
          trackGoogleEvent({
            eventName: 'onboarding_relationship_goal_opened',
          });
          break;
        }
        case QuizStep.LONG_TERM: {
          trackGoogleEvent({
            eventName: 'onboarding_looking_for_long_relationship_opened',
          });
          break;
        }
        case QuizStep.PARTNER_BIRTH_TIME_KNOWLEDGE: {
          trackGoogleEvent({
            eventName: 'onboarding_partner_birthtime_knowledge_opened',
          });
          break;
        }
        case QuizStep.PARTNER_NO_PROBLEM: {
          trackGoogleEvent({
            eventName: 'onboarding_partner_no_problem_opened',
          });
          break;
        }
        case QuizStep.PARTNER_BIRTH_TIME: {
          trackGoogleEvent({
            eventName: 'onboarding_partner_birthtime_opened',
          });
          break;
        }
        case QuizStep.PARTNER_HOMETOWN: {
          trackGoogleEvent({
            eventName: 'onboarding_partner_hometown_opened',
          });
          break;
        }
        case QuizStep.DISCUSSING_PROBLEMS: {
          trackGoogleEvent({
            eventName: 'onboarding_discussing_all_problem_opened',
          });
          break;
        }
        case QuizStep.AFFECT_RELATIONSHIP: {
          trackGoogleEvent({
            eventName: 'onboarding_factors_affect_relationship_opened',
          });
          break;
        }
        case QuizStep.SEX_RELATIONSHIP: {
          trackGoogleEvent({
            eventName: 'onboarding_sex_priority_relationship_opened',
          });
          break;
        }
        case QuizStep.FINAL_SUMMARY: {
          trackGoogleEvent({ eventName: 'onboarding_build_report_opened' });
          break;
        }
        case QuizStep.ENTER_EMAIL: {
          trackGoogleEvent({ eventName: 'onboarding_email_opened' });
          break;
        }
        case QuizStep.ANIMATION_SCREENS: {
          trackGoogleEvent({
            eventName: 'onboarding_animation_screens_open',
          });
          break;
        }
      }
    }
  }, [step, isStepIndexValid, trackGoogleEvent]);

  let showStepHeader = true;
  let stepContent: ReactNode;
  let titleClassName: string | undefined;
  let containerClassName: string | undefined;
  let bodyClassName = styles.defaultBodyClassName;
  let themeColor = '#181926';
  let hideGoPrevious = false;

  switch (step?.type) {
    case QuizStepType.OTHER: {
      switch (step.step) {
        case QuizStep.WELCOME: {
          themeColor = '#000000';
          bodyClassName = styles.blackBodyClassName;
          containerClassName = styles.welcomeContainerClassName;
          showStepHeader = false;
          stepContent = (
            <WelcomeScreen
              onChange={(value) => {
                quizStore.setWelcome(value);
                trackGoogleEvent({
                  eventName: 'onboarding_ask_relationship_chosen',
                  options: { answer: value },
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        case QuizStep.WELCOME_AB_TEST: {
          themeColor = '#000000';
          bodyClassName = styles.blackBodyClassName;
          containerClassName = styles.welcomeContainerClassName;
          showStepHeader = false;
          stepContent = (
            <WelcomeScreenABTest
              onChange={(value) => {
                quizStore.setWelcome(value);
                trackGoogleEvent({
                  eventName: 'onboarding_remove_loneliness_chosen',
                  options: { answer: value },
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        case QuizStep.WELCOME_FOUR_VARIANTS_AB_TEST: {
          themeColor = '#000000';
          bodyClassName = styles.orangeBodyClassName;
          containerClassName = styles.welcomeContainerClassName;
          showStepHeader = false;
          stepContent = (
            <WelcomeScreenFourVariantsABTest
              onChange={(value) => {
                quizStore.setWelcome(value);
                trackGoogleEvent({
                  eventName: 'onboarding_find_perfect_match_chosen',
                  options: { answer: value },
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        case QuizStep.WELCOME_THREE_VARIANTS_AB_TEST: {
          themeColor = '#000000';
          bodyClassName = styles.zodiacBodyClassName;
          containerClassName = styles.welcomeContainerClassName;
          showStepHeader = false;
          stepContent = (
            <WelcomeScreenThreeVariantsABTest
              onChange={(value) => {
                quizStore.setWelcome(value);
                trackGoogleEvent({
                  eventName: 'onboarding_eclipses_work_u_chosen',
                  options: { answer: value },
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        case QuizStep.INTRO: {
          themeColor = '#000000';
          bodyClassName = styles.blackBodyClassName;
          showStepHeader = false;
          stepContent = (
            <IntroStep
              onChange={() => {
                trackGoogleEvent({
                  eventName: 'onboarding_intro_chosen',
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        case QuizStep.WE_GOT_YOU: {
          themeColor = '#000000';
          bodyClassName = styles.blackBodyClassName;
          showStepHeader = false;
          stepContent = (
            <WeGotYouStep
              onChange={() => {
                trackGoogleEvent({
                  eventName: 'onboarding_we_got_you_chosen',
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        case QuizStep.TAKE_COMMAND: {
          themeColor = '#000000';
          bodyClassName = styles.blackBodyClassName;
          showStepHeader = false;
          stepContent = (
            <TakeCommand
              onChange={() => {
                trackGoogleEvent({
                  eventName: 'onboarding_command_your_life_chosen',
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        case QuizStep.NAME: {
          const handleChange = (value: string) => {
            quizStore.setName(value);
            trackGoogleEvent({
              eventName: 'onboarding_name_chosen',
              options: {
                name: value,
              },
            });
            goToNextStep();
          };
          stepContent = (
            <NameStep
              key={step.step}
              value={quizStore.name}
              onChange={handleChange}
            />
          );
          break;
        }
        case QuizStep.DISCOVER_SLIDER: {
          showStepHeader = false;
          bodyClassName = styles.discoverBackground;
          stepContent = (
            <DiscoverSliderStep
              onChange={() => {
                trackGoogleEvent({
                  eventName: 'onboarding_discover_slider_chosen',
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        case QuizStep.DATE_OF_BIRTH: {
          const handleChange = (value: Date) => {
            quizStore.setDateOfBirth(value);
            trackGoogleEvent({
              eventName: 'onboarding_date_of_birth_chosen',
              options: { answer: dayjs(value).format('YYYY-MM-DD') },
            });
            goToNextStep();
          };
          stepContent = (
            <DateOfBirthStep
              value={quizStore.dateOfBirth}
              onChange={handleChange}
            />
          );
          break;
        }
        case QuizStep.HOMETOWN: {
          showStepHeader = false;
          const handleHometownChange = (
            value: string,
            latitude: number,
            longitude: number,
          ) => {
            quizStore.setHometown(value, latitude, longitude);
            trackGoogleEvent({
              eventName: 'onboarding_hometown_chosen',
              options: { answer: value },
            });
          };
          stepContent = (
            <HometownStep
              title={step.title}
              subtitle={step.subtitle}
              value={quizStore.hometown}
              onChange={handleHometownChange}
              goToNextStep={() => goToNextStep()}
            />
          );
          break;
        }
        case QuizStep.BIRTH_TIME: {
          const handleTime = (value: string) => {
            quizStore.setBirthtime(value);
            trackGoogleEvent({
              eventName: 'onboarding_birthtime_chosen',
              options: { answer: value },
            });
            goToNextStep();
          };
          stepContent = (
            <BirthtimeStep value={quizStore.birthtime} onChange={handleTime} />
          );
          break;
        }
        case QuizStep.SIGN_HELPED: {
          containerClassName = styles.containerSignHelped;
          stepContent = (
            <SignHelped
              onNextRequest={() => {
                trackGoogleEvent({
                  eventName: 'onboarding_sign_helped_chosen',
                });
                goToNextStep();
              }}
            />
          );
          showStepHeader = false;
          break;
        }
        case QuizStep.PARTNER_HOMETOWN: {
          showStepHeader = false;
          const handlePartnerHometownChange = (
            value: string,
            latitude: number,
            longitude: number,
          ) => {
            quizStore.setPartnerHometown(value, latitude, longitude);
            trackGoogleEvent({
              eventName: 'onboarding_partner_hometown_chosen',
              options: { answer: value },
            });
          };
          stepContent = (
            <HometownStep
              title={step.title}
              subtitle={step.subtitle}
              value={quizStore.partnerHometown}
              onChange={handlePartnerHometownChange}
              goToNextStep={() => goToNextStep()}
            />
          );
          break;
        }
        case QuizStep.PARTNER_BIRTH_TIME: {
          const handlePartnerTime = (value: string) => {
            quizStore.setPartnerBirthtime(value);
            trackGoogleEvent({
              eventName: 'onboarding_partner_birthtime_chosen',
              options: { answer: value },
            });
            goToNextStep();
          };
          stepContent = (
            <BirthtimeStep
              value={quizStore.partnerBirthtime}
              onChange={handlePartnerTime}
            />
          );
          break;
        }
        case QuizStep.BEST_MATCHES: {
          containerClassName = styles.smallerPaddingTopContainer;
          stepContent = (
            <BestMatchesStep
              goToNextStep={() => {
                trackGoogleEvent({
                  eventName: 'onboarding_best_matches_chosen',
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        case QuizStep.PARTNER_ASTROLOGY_SIGN: {
          const handlePartnerSign = (value: Date) => {
            quizStore.setPartnerAstrologySign(value);
            trackGoogleEvent({
              eventName: 'onboarding_partner_date_of_birth_chosen',
              options: { answer: dayjs(value).format('YYYY-MM-DD') },
            });
            goToNextStep();
          };
          stepContent = (
            <DateOfBirthStep
              value={quizStore.partnerAstrologySign}
              onChange={handlePartnerSign}
            />
          );
          break;
        }
        case QuizStep.PARTNER_COMPATIBILITY: {
          containerClassName = styles.smallerPaddingTopContainer;
          stepContent = (
            <PartnerCompatibilityStep
              goToNextStep={() => {
                trackGoogleEvent({
                  eventName: 'onboarding_compatibility_chosen',
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        case QuizStep.FINAL_SUMMARY: {
          containerClassName = styles.containerFinalSummaryStep;
          stepContent = (
            <FinalSummaryStep
              onNextRequest={() => {
                trackGoogleEvent({
                  eventName: 'onboarding_build_report_chosen',
                });
                goToNextStep();
              }}
            />
          );
          showStepHeader = false;
          break;
        }
        case QuizStep.ENTER_EMAIL: {
          titleClassName = styles.emailTitle;
          stepContent = <EnterEmailStep goToNextStep={goToNextStep} />;
          break;
        }
        case QuizStep.ANIMATION_SCREENS: {
          showStepHeader = false;
          bodyClassName = styles.nebulaBodyClassName;
          stepContent = <AnimatedScreensStep goToNextStep={goToNextStep} />;
          break;
        }
        case QuizStep.PERSONAL_PLAN_HIGHLIGHTS: {
          showStepHeader = false;
          stepContent = (
            <PersonalPlanHighlights
              userSign={quizStore.getAstrologySing()}
              goToNextStep={() => {
                trackGoogleEvent({
                  eventName: 'onboarding_highlights_your_plan_tap',
                });
                goToNextStep();
              }}
            />
          );
          break;
        }
        default: {
          throw new Error('Unexpected step with type OTHER');
        }
      }
      break;
    }
    case QuizStepType.SINGLE_OPTION_SELECT: {
      let optionsContainerClassName: string | undefined;
      let onChange: (value: number) => void;
      let googleAnalyticsEventName: string;
      switch (step.step) {
        case QuizStep.FACING_REPEATING: {
          hideGoPrevious = true;
          onChange = (value) => quizStore.setFacingRepeating(value);
          googleAnalyticsEventName = 'onboarding_repeating_pattern_chosen';
          break;
        }
        case QuizStep.ASPECT: {
          onChange = (value) => quizStore.setAspect(value);
          googleAnalyticsEventName = 'onboarding_aspect_relationship_chosen';
          break;
        }
        case QuizStep.DESIRED_RELATIONSHIP: {
          onChange = (value) => quizStore.setDesiredRelationship(value);
          googleAnalyticsEventName =
            'onboarding_desired_relationship_type_chosen';
          break;
        }
        case QuizStep.PREVIOUS_RELATIONSHIP: {
          onChange = (value) => quizStore.setPreviousRelationship(value);
          googleAnalyticsEventName =
            'onboarding_describes_prev_relationship_chosen';
          break;
        }
        case QuizStep.TOXIC_RELATIONSHIP: {
          onChange = (value) => quizStore.setToxicRelationship(value);
          googleAnalyticsEventName =
            'onboarding_toxic_relationship_aspect_chosen';
          break;
        }
        case QuizStep.IN_LOVE: {
          onChange = (value) => quizStore.setInLove(value);
          googleAnalyticsEventName = 'onboarding_love_now_chosen';
          break;
        }
        case QuizStep.ROMANTIC_FEELINGS: {
          onChange = (value) => quizStore.setRomanticFeelings(value);
          googleAnalyticsEventName = 'onboarding_navigate_romantic_chosen';
          break;
        }
        case QuizStep.KARMIC_LESSONS: {
          onChange = (value) => quizStore.setKarmicLessons(value);
          googleAnalyticsEventName = 'onboarding_know_karmic_chosen';
          break;
        }
        case QuizStep.PURPOSE: {
          onChange = (value) => quizStore.setPurpose(value);
          googleAnalyticsEventName = 'onboarding_your_life_purpose_chosen';
          break;
        }
        case QuizStep.JOB_LOVING: {
          onChange = (value) => quizStore.setJobLoving(value);
          googleAnalyticsEventName = 'onboarding_you_happy_your_job_chosen';
          break;
        }
        case QuizStep.FINANCIAL_GOAL: {
          onChange = (value) => quizStore.setFinancialGoal(value);
          googleAnalyticsEventName = 'onboarding_financial_goal_chosen';
          break;
        }
        case QuizStep.LIFE_ASPECTS: {
          onChange = (value) => quizStore.setLifeAspects(value);
          googleAnalyticsEventName = 'onboarding_what_add_your_profile_chosen';
          break;
        }
        case QuizStep.WORK_OUT: {
          onChange = (value) => quizStore.setWorkOut(value);
          googleAnalyticsEventName = 'onboarding_what_issues_work_chosen';
          break;
        }
        case QuizStep.SPIRITUAL_GOAL: {
          onChange = (value) => quizStore.setSpiritualGoal(value);
          googleAnalyticsEventName = 'onboarding_lifes_spiritual_goal_chosen';
          break;
        }
        case QuizStep.LIFE: {
          onChange = (value) => quizStore.setLife(value);
          googleAnalyticsEventName = 'onboarding_life_now_chosen';
          break;
        }
        case QuizStep.MOOD: {
          onChange = (value) => quizStore.setMood(value);
          googleAnalyticsEventName = 'onboarding_felling_today_chosen';
          break;
        }
        case QuizStep.CRITICS_SENSITIVE: {
          onChange = (value) => quizStore.setCriticsSensitive(value);
          googleAnalyticsEventName = 'onboarding_sensitive_disapproval_chosen';
          break;
        }
        case QuizStep.HOT_TEMPRED: {
          onChange = (value) => quizStore.setHotTempred(value);
          googleAnalyticsEventName = 'onboarding_hot_tempered_person_chosen';
          break;
        }
        case QuizStep.MOOD_SWINGS: {
          onChange = (value) => quizStore.setMoodSwings(value);
          googleAnalyticsEventName = 'onboarding_experience_mood_chosen';
          break;
        }
        case QuizStep.GENDER: {
          onChange = (value) => quizStore.setGender(value);
          googleAnalyticsEventName = 'onboarding_gender_chosen';
          break;
        }
        case QuizStep.BIRTH_TIME_KNOWLEDGE: {
          onChange = (value) => quizStore.setBirthtimeKnowledge(value);
          googleAnalyticsEventName = 'onboarding_birthtime_knowledge_chosen';
          break;
        }
        case QuizStep.RELATIONSHIP: {
          onChange = (value) => quizStore.setRelationship(value);
          googleAnalyticsEventName = 'onboarding_relationship_chosen';
          optionsContainerClassName = styles.optionsContainerWithIcon;
          break;
        }
        case QuizStep.SEX: {
          onChange = (value) => quizStore.setSex(value);
          googleAnalyticsEventName = 'onboarding_sex_priority_chosen';
          break;
        }
        case QuizStep.COMFORTABLE: {
          onChange = (value) => quizStore.setComfortable(value);
          googleAnalyticsEventName =
            'onboarding_comfortable_unfamiliar_people_chosen';
          break;
        }
        case QuizStep.GOOD_ENOUGH: {
          onChange = (value) => quizStore.setGoodEnough(value);
          googleAnalyticsEventName = 'onboarding_good_enough_chosen';
          break;
        }
        case QuizStep.RELATIONSHIP_GOAL: {
          onChange = (value) => quizStore.setRelationshipGoal(value);
          googleAnalyticsEventName = 'onboarding_relationship_goal_chosen';
          break;
        }
        case QuizStep.LONG_TERM: {
          onChange = (value) => quizStore.setLongTerm(value);
          googleAnalyticsEventName =
            'onboarding_looking_for_long_relationship_chosen';
          break;
        }
        case QuizStep.PARTNER_BIRTH_TIME_KNOWLEDGE: {
          onChange = (value) => quizStore.setPartnerBirthtimeKnowledge(value);
          googleAnalyticsEventName =
            'onboarding_partner_birthtime_knowledge_chosen';
          break;
        }
        case QuizStep.DISCUSSING_PROBLEMS: {
          onChange = (value) => quizStore.setDiscussingProblems(value);
          googleAnalyticsEventName = 'onboarding_discussing_all_problem_chosen';
          break;
        }
        case QuizStep.SEX_RELATIONSHIP: {
          onChange = (value) => quizStore.setSexRelationship(value);
          googleAnalyticsEventName =
            'onboarding_sex_priority_relationship_chosen';
          break;
        }
        case QuizStep.EMOTIONS: {
          onChange = (value) => quizStore.setEmotions(value);
          googleAnalyticsEventName = 'onboarding_emotions_chosen';
          optionsContainerClassName = styles.optionsContainerWithIcon;
          break;
        }
        case QuizStep.AFFECT_RELATIONSHIP: {
          onChange = (value) => quizStore.setAffectRelationship(value);
          googleAnalyticsEventName =
            'onboarding_factors_affect_relationship_chosen';
          break;
        }
        default: {
          throw new Error('Unexpected step with type SINGLE_OPTION_SELECT');
        }
      }
      stepContent = (
        <SingleOptionSelectStep
          prefix={step.step}
          options={step.options}
          optionsContainerClassName={optionsContainerClassName}
          value={quizStore.getSingleOptionSelectStepValue(step.step)}
          goToNextStep={goToNextStep}
          skipOption={step.skipOption}
          nextButtonText={step.nextButtonText}
          onChange={(value) => {
            trackGoogleEvent({
              eventName: googleAnalyticsEventName,
              options: { answer: value },
            });
            onChange(value);
          }}
        />
      );
      break;
    }
    // case QuizStepType.MULTIPLE_OPTIONS_SELECT: {
    //   let optionsContainerClassName: string | undefined
    //   let value: readonly string[] | undefined
    //   let onChange: (value?: readonly string[]) => void
    //   let googleAnalyticsEventName: string
    //
    //   switch (step?.step) {
    //     default:
    //       throw new Error('Unexpected step with type MULTIPLE_OPTIONS_SELECT')
    //   }
    //   stepContent = (
    //     <MultipleOptionSelectStep
    //       suffix={step.step}
    //       options={step.options}
    //       optionsContainerClassName={optionsContainerClassName}
    //       value={value}
    //       skipOption={step.skipOption}
    //       nextButtonText={step.nextButtonText}
    //       onChange={onChange}
    //       onSubmit={(value) => {
    //         trackGoogleEvent({
    //           eventName: googleAnalyticsEventName,
    //           properties: Object.fromEntries(
    //             step.options.map((option) => [
    //               option.value,
    //               value!.includes(option.value),
    //             ])
    //           ),
    //         })
    //         goToNextStep()
    //       }}
    //       goToNextStep={() => goToNextStep()}
    //     />
    //   )
    //
    //   break
    // }
    case QuizStepType.TEXT_CARD_WITH_BACKGROUND_IMAGE: {
      showStepHeader = false;
      let googleAnalyticsEventName: string;
      switch (step.step) {
        case QuizStep.GREETINGS: {
          bodyClassName = styles.greetingsBackground;
          googleAnalyticsEventName = 'onboarding_welcome_chosen';
          break;
        }
        case QuizStep.NO_PROBLEM: {
          bodyClassName = styles.noProblemBackground;
          googleAnalyticsEventName = 'onboarding_no_problem_chosen';
          break;
        }
        case QuizStep.PARTNER_NO_PROBLEM: {
          bodyClassName = styles.noProblemBackground;
          googleAnalyticsEventName = 'onboarding_partner_no_problem_chosen';
          break;
        }
        default: {
          throw new Error(
            'Unexpected step with type TEXT_CARD_WITH_BACKGROUND_IMAGE',
          );
        }
      }

      stepContent = (
        <TextCardWithBackgroundStep
          TextContentComponent={step.TextContentComponent}
          title={step.TitleComponent ? <step.TitleComponent /> : step.title}
          nextButtonText={step.nextButtonText}
          onClick={() => {
            trackGoogleEvent({
              eventName: googleAnalyticsEventName,
            });
            goToNextStep();
          }}
        />
      );
      break;
    }
  }

  const isProgressShown = isStepProgressShown(step);

  const calculateProgress = ({ quizSection }: { quizSection: QuizSection }) => {
    const sortedQuizSection = steps.filter(
      (step) => step.section === quizSection,
    );

    if (
      steps.indexOf(step) >
      // @ts-expect-error
      steps.indexOf(sortedQuizSection.at(-1))
    ) {
      return 1;
    }

    return isProgressShown && sortedQuizSection
      ? sortedQuizSection.indexOf(step) /
          getStepsWithShownProgressCount(sortedQuizSection)
      : 0;
  };

  useEffect(() => {
    document.body.classList.add(bodyClassName);
    return () => {
      document.body.classList.remove(bodyClassName);
    };
  }, [bodyClassName]);

  return (
    <>
      <Title>Quiz</Title>
      <Head>
        <meta name="theme-color" content={themeColor} />
      </Head>
      {!isReady || !step ? (
        <PageLoading />
      ) : (
        <>
          {isProgressShown && (
            <OnboardingHeader
              hideGoPrevious={hideGoPrevious}
              goToPreviousStep={goToPreviousStep}
              step={step}
              canSkipQuiz={canSkipQuiz}
              handleQuizSkipClick={handleQuizSkipClick}
              calculateProgress={calculateProgress}
              country={quizStore.country}
            />
          )}
          <Container
            as="main"
            className={classNames(styles.container, containerClassName)}>
            {showStepHeader && (
              <StepHeader
                titleClassName={titleClassName}
                title={step.title}
                subtitle={step.subtitle}
              />
            )}
            {stepContent}
          </Container>
        </>
      )}
    </>
  );
});

export default nonMemberPage(Quiz);
